import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { DiscountGroup } from '../../../../generated/generated-types';
import { DiscountGroupCustomerGroupRelationWidgetComponent } from '../discount-group-customer-group-relation-widget/discount-group-customer-group-relation-widget.component';
import { DiscountGroupCustomerRelationWidgetComponent } from '../discount-group-customer-relation-widget/discount-group-customer-relation-widget.component';
import { DiscountGroupDocumentRelationWidgetComponent } from '../discount-group-document-relation-widget/discount-group-document-relation-widget.component';
import { DiscountGroupVendorGroupRelationWidgetComponent } from '../discount-group-vendor-group-relation-widget/discount-group-vendor-group-relation-widget.component';
import { DiscountGroupVendorRelationWidgetComponent } from '../discount-group-vendor-relation-widget/discount-group-vendor-relation-widget.component';

@Component({
  selector: 'app-formly-discount-group-relations-document-based-widget',
  templateUrl: './formly-discount-group-relations-document-based-widget.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TabsModule,
    DiscountGroupCustomerRelationWidgetComponent,
    DiscountGroupCustomerGroupRelationWidgetComponent,
    DiscountGroupVendorRelationWidgetComponent,
    DiscountGroupVendorGroupRelationWidgetComponent,
    DiscountGroupDocumentRelationWidgetComponent,
    TranslatePipe,
    CommonModule,
    BlockableDiv,
    ButtonModule,
    TooltipModule,
  ]
})
export class FormlyDiscountGroupRelationsDocumentBasedWidgetComponent extends FieldType<FieldTypeConfig> {

  private readonly cdRef = inject(ChangeDetectorRef);

  isSaving = signal(false);

  /** Survives brief model/form clears after save (stream reload / route id sync). */
  private readonly stickyDiscountGroupId = signal<string | null>(null);

  get discountGroupId(): string | null {
    return this.model?.id ?? this.formControl?.value ?? this.stickyDiscountGroupId();
  }

  get isBlocked(): boolean {
    // Stay blocked for the whole save — avoids widgets flashing before save finishes.
    return this.isSaving() || !this.discountGroupId;
  }

  checkIfCanSave(): boolean {
    const component = this.options.formState.detailComponent as BaseDetailComponent<DiscountGroup>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode(): Promise<void> {
    const component = this.options.formState.detailComponent as BaseDetailComponent<DiscountGroup>;
    if (!component) {
      console.error('No detail component found to save DiscountGroup');
      return;
    }

    this.isSaving.set(true);
    this.cdRef.detectChanges();

    try {
      const saved = await component.save();
      const savedId = saved?.id ?? this.model?.id ?? null;
      if (savedId) {
        this.stickyDiscountGroupId.set(savedId);
      }
    } finally {
      this.isSaving.set(false);
      this.cdRef.detectChanges();
    }
  }

}
